import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, computed, inject, output, signal } from '@angular/core';

import { Auth } from '../../../../core/auth/auth';
import { UserGroupAccess } from '../../../../core/models/user';

/**
 * A pending access switch, emitted when the user picks another group.
 *
 * Carries both ends so the confirm dialog can say what is being left as well
 * as what is being entered.
 */
export interface AccessSwitchRequest {
  from: UserGroupAccess | null;
  to: UserGroupAccess;
}

/**
 * Header access (user group) switcher.
 *
 * Shown only for a login that holds more than one group (`group_ids` carries
 * more than one entry) - single-access users never see it. Like the company
 * switcher it is presentational: it emits the picked group and the parent owns
 * the confirm dialog and the switch itself, so the flow lives in one place.
 */
@Component({
  selector: 'app-access-switcher',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './access-switcher.html',
  styleUrls: ['../company-switcher/company-switcher.scss', './access-switcher.scss'],
})
export class AccessSwitcher {
  private readonly auth = inject(Auth);
  private readonly elementRef = inject(ElementRef);

  /** Emitted when the user picks a group. The parent raises the confirm modal. */
  readonly accessSelected = output<AccessSwitchRequest>();
  /** Emitted when the panel opens, so sibling header dropdowns can close. */
  readonly opened = output<void>();

  readonly isOpen = signal<boolean>(false);

  readonly options = computed<UserGroupAccess[]>(
    () => this.auth.currentUser()?.group_ids ?? [],
  );

  /** Whether to render anything at all: only a login holding several groups. */
  readonly isVisible = computed<boolean>(() => this.options().length > 1);

  /** The active group is the option whose id matches the stored `group_id`. */
  readonly activeGroup = computed<UserGroupAccess | null>(() => {
    const activeId = Number(this.auth.currentUser()?.group_id);
    return this.options().find((option) => Number(option.group_id) === activeId) ?? null;
  });

  readonly activeLabel = computed<string>(() => this.activeGroup()?.label ?? 'Switch access');

  isActive(option: UserGroupAccess): boolean {
    return Number(option.group_id) === Number(this.activeGroup()?.group_id);
  }

  initialOf(option: UserGroupAccess): string {
    return option.label?.charAt(0)?.toUpperCase() || 'R';
  }

  toggleDropdown(): void {
    const next = !this.isOpen();
    this.isOpen.set(next);
    if (next) this.opened.emit();
  }

  closeDropdown(): void {
    this.isOpen.set(false);
  }

  selectGroup(option: UserGroupAccess): void {
    if (this.isActive(option)) return;
    this.closeDropdown();
    this.accessSelected.emit({ from: this.activeGroup(), to: option });
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (this.isOpen() && !this.elementRef.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen()) this.closeDropdown();
  }
}
